import '../../css/LinePlot.less';

import * as d3 from 'd3';
import _ from 'lodash';
import memoize from 'memoize-one';
import React, {
  FC,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  AreaSeries,
  Borders,
  FlexibleXYPlot,
  GradientDefs,
  HorizontalGridLines,
  LineSeries,
  LineSeriesCanvas,
  LineSeriesPoint,
  HeatmapSeries,
  MarkSeries,
  MarkSeriesPoint,
  XAxis,
  YAxis,
  MarkSeriesCanvas,
  LineSeriesProps,
} from 'react-vis';
import LinePlotCrosshair from './LinePlotCrosshair';
import {
  Line,
  convertTimestepToSeconds,
  Timestep,
  getPlotMargin,
  formatXAxisNonTime,
  formatYAxis,
  YAxisType,
  XAxisType,
  axisFontStyles,
} from '../../util/plotHelpers';
import {truncateString} from '../../util/runhelpers';
import {HeatmapSeriesCanvas} from './HeatmapSeries';
import FancyLegend from './FancyLegend';
import {markToIcon} from '../LineStylePicker';
import classNames from 'classnames';
import {makePropsAreEqual} from '../../util/shouldUpdate';
import DelayedRender from '../../containers/DelayedRender';
import makeComp from '../../util/profiler';

interface LinePlotPlotProps {
  domRef: React.RefObject<HTMLDivElement>;
  xAxis: string;
  yScale: YAxisType;
  lines: Line[];
  xScale: 'linear' | 'log';
  yAxis: string;
  xDomain: number[];
  yDomain: number[];
  lastDrawLocation?: any;
  xAxisTitle?: string;
  yAxisTitle?: string;

  monotonic?: boolean;
  svg?: boolean;
}

type AllLinePlotPlotProps = LinePlotPlotProps &
  ReturnType<typeof useLinePlotPlotProps>;

const LinePlotPlotComp: FC<AllLinePlotPlotProps> = makeComp(
  ({
    domRef,
    xAxis,
    yScale,
    lines,
    xScale,
    yAxis,
    xDomain,
    yDomain,
    xAxisTitle,
    yAxisTitle,
    svg,
    highlightRun,
  }: AllLinePlotPlotProps) => {
    let xType: XAxisType = 'linear';
    if (xAxis === 'Wall Time') {
      xType = 'time';
    } else if (xScale === 'log') {
      xType = 'log';
    }

    // if user passed in multiple heatmaps only display the first one and nothing else
    const heatmaps = lines.filter(line => line.type === 'heatmap');
    if (heatmaps.length > 0) {
      lines = [heatmaps[0]];
    }

    // Swap to canvas or SVG based on rendering mode
    const isSVG = svg ?? false;
    const HeatmapSeriesComponent = isSVG ? HeatmapSeries : HeatmapSeriesCanvas;

    const LineSeriesComponent: React.SFC<LineSeriesProps> = isSVG
      ? (LineSeries as any)
      : LineSeriesCanvas;

    const yAxisTickTotal = yScale === 'log' ? 2 : 5;

    return (
      <FlexibleXYPlot
        ref={domRef as any}
        margin={getPlotMargin(
          {xAxis, yAxis},
          {yAxis: yDomain},
          {yAxis: yScale},
          {},
          {yAxis: yAxisTickTotal}
        )}
        xDomain={xDomain}
        yDomain={yDomain}
        yType={yScale}
        xType={xType}
        padding={{top: 10}}>
        <HorizontalGridLines
          style={{strokeWidth: 0.5}}
          tickTotal={yScale === 'log' ? 3 : 5}
        />

        {lines
          .map((line, i) => {
            let strokeDashArray: any =
              line.mark === 'dashed'
                ? [4, 2]
                : line.mark === 'dotted'
                ? [1, 1]
                : line.mark === 'dotdash'
                ? [5, 2, 1, 2]
                : line.mark === 'dotdotdash'
                ? [5, 2, 1, 2, 1, 2]
                : undefined;
            strokeDashArray = isSVG
              ? strokeDashArray?.join(',')
              : strokeDashArray;

            return line.type === 'area' ? (
              <AreaSeries
                key={i}
                color={line.color}
                data={line.data}
                getNull={d => d.y !== null}
                stroke={'#0000'}
              />
            ) : line.type === 'scatter' ? (
              <MarkSeries
                key={i}
                color={line.color}
                data={line.data as MarkSeriesPoint[]}
                getNull={d => d.y !== null}
                size={2}
              />
            ) : line.type === 'heatmap' ? (
              <HeatmapSeriesComponent
                key={i}
                colorRange={['#f0f0f0', line.color]}
                data={line.data}
                size={2}
              />
            ) : line.type === 'points' || line.mark === 'points' ? (
              <MarkSeriesCanvas
                key={i}
                color={line.color}
                data={line.data as LineSeriesPoint[]}
                size={2}
              />
            ) : (
              <LineSeriesComponent
                key={i}
                color={line.color}
                data={line.data as LineSeriesPoint[]}
                getNull={d => d.y !== null}
                size={2}
                strokeDasharray={strokeDashArray}
                strokeStyle={'solid'}
                strokeWidth={
                  line.run && highlightRun === line.run.name ? 3 : 1.5
                }
              />
            );
          })
          .reverse() // reverse so the top line is the first line
        }
        {lines.map((line, i) => {
          if (
            line.type === 'area' ||
            line.type === 'scatter' ||
            line.type === 'heatmap'
          ) {
            return null;
          }

          // add a dot mark to the end of each line
          return (
            <MarkSeries
              key={i}
              color={line.color}
              data={[line.data[line.data.length - 1] as MarkSeriesPoint]}
              getNull={d => d.y !== null}
              size={2}
            />
          );
        })}
        <GradientDefs>
          <linearGradient id="leftFadeGradient" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="white" stopOpacity={1} />
            <stop offset="60%" stopColor="white" stopOpacity={1} />
            <stop offset="100%" stopColor="white" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="rightFadeGradient" x1="1" x2="0" y1="0" y2="0">
            <stop offset="0%" stopColor="white" stopOpacity={1} />
            <stop offset="100%" stopColor="white" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="bottomFadeGradient" x1="0" x2="0" y1="1" y2="0">
            <stop offset="0%" stopColor="white" stopOpacity={1} />
            <stop offset="40%" stopColor="white" stopOpacity={1} />
            <stop offset="100%" stopColor="white" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="topFadeGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity={1} />
            <stop offset="100%" stopColor="white" stopOpacity={0} />
          </linearGradient>
        </GradientDefs>
        <Borders
          className="plot-border"
          style={{
            left: {fill: 'url(#leftFadeGradient)', opacity: 1},
            right: {fill: 'url(#rightFadeGradient)', opacity: 1},
            bottom: {fill: 'url(#bottomFadeGradient)', opacity: 1},
            top: {fill: 'url(#topFadeGradient)', opacity: 1},
          }}
        />

        <XAxis
          // This is the line that marks the x axis
          tickTotal={0}
          on0
        />

        <XAxis
          // This is the legend
          title={xAxisTitle || truncateString(xAxis)}
          style={{
            ...axisFontStyles,
            line: {strokeWidth: 0},
          }}
          tickFormat={formatXAxisNonTime(xType, xDomain[0], xDomain[1])}
          // React vis (or maybe d3) doesn't seem to respect the ticktotal
          // well in the case of log scale, so smaller vale is safer.  See WB-4525
          tickTotal={xType === 'log' ? 3 : Math.min(5, xDomain[1])}
        />

        <XAxis
          // These are the little tick marks
          width={0}
          tickTotal={xType === 'log' ? 3 : Math.min(5, xDomain[1])}
          tickFormat={tick => ''}
        />

        <YAxis
          title={yAxisTitle}
          tickFormat={tick => formatYAxis(tick)}
          tickTotal={yAxisTickTotal}
          style={{
            ...axisFontStyles,
            line: {stroke: 'none'},
          }}
        />
      </FlexibleXYPlot>
    );
  },
  {
    id: 'LinePlotPlotComp',
    memo: makePropsAreEqual({
      name: 'LinePlotPlot',
      deep: ['yDomain', 'xDomain'],
      ignoreFunctions: true,
      debug: false,
      verbose: false,
    }),
  }
);

function useLinePlotPlotProps(props: LinePlotPlotProps) {
  // Global run highlighting is disabled. If you uncomment this, and the
  // equivalent in RunSelectorRowHeader.tsx, we'll use a global highlight to show which
  // run is currently being hovered. There are perf issues so it's not enabled
  // yet.
  //
  // Issues:
  //    - line highlighting here requires the line plot to rerender all the lines, which
  //      is slow if it happens frequently (one solution is to debounce the highlight)
  //    - shared highlighting doesn't work when grouping is on
  //
  // const highlightRun = InteractStateContext.useInteractState(
  //  props.domRef,
  //  interactState => interactState.highlight['run:name']
  // ) as string | undefined;
  // return {highlightRun};
  return {highlightRun: undefined};
}

const LinePlotPlot = makeComp(
  (props: LinePlotPlotProps) => {
    const selectedProps = useLinePlotPlotProps(props);
    return <LinePlotPlotComp {...props} {...selectedProps} />;
  },
  {id: 'LinePlotPlot'}
);

interface LinePlotProps {
  entityName?: string;
  projectName?: string;
  xDomain?: Array<number | null | undefined>;
  yDomain?: Array<number | null | undefined>;
  lines: Line[];
  timestep?: Timestep;
  xScale?: 'linear' | 'log';
  yScale?: 'linear' | 'log';
  showLegend?: boolean;
  disableRunLinks?: boolean;
  xAxis: string; // Name of the xAxis
  yAxis: string; // Name of the yAxis
  xAxisTitle?: string; // optional string for exactly this xaxis title displayed in graph
  yAxisTitle?: string; // optional string for exactly this yaxis title displayed in graph
  legendPrefix?: React.Component | JSX.Element;
  ignoreOutliers?: boolean;
  zooming?: boolean; // is zooming happening
  svg?: boolean;
  overrideLineTitles?: {
    [key: string]: string;
  };
  overrideColors?: {
    [key: string]: {
      color: string;
      transparentColor: string;
    };
  };
  overrideMark?: {
    [key: string]: string;
  };
  setYScale?(yScale: string): void;
  zoomCallback?(xAxisMin?: number, xAxisMax?: number): void;
}

const LinePlot: FC<LinePlotProps> = makeComp(
  ({
    xDomain,
    yDomain,
    lines,
    timestep,
    xScale,
    yScale,
    showLegend,
    disableRunLinks,
    xAxis,
    yAxis,
    xAxisTitle,
    yAxisTitle,
    legendPrefix,
    ignoreOutliers,
    svg,
    zoomCallback,
  }: LinePlotProps) => {
    const domRef = useRef<HTMLDivElement | null>(null);
    const [hideCrosshair, setHideCrosshair] = useState(false);
    const [lastDrawLocation, setLastDrawLocation] = useState<any | undefined>();
    const [, setRecentResize] = useState(true);
    const [zoomedXAxis, setZoomedXAxis] = useState(false);
    const [zoomedYAxis, setZoomedYAxis] = useState(false);

    const calcDomain = useMemo(
      () =>
        memoize(
          (
            linesCalcDomain: Line[],
            xDomainCalcDomain,
            yDomainCalcDomain,
            lastDrawLocationCalcDomain,
            ignoreOutliersCalcDomain,
            zoomedXAxisCalcDomain,
            zoomedYAxisCalcDomain
          ) => {
            let retYDomain =
              yDomainCalcDomain != null ? [...yDomainCalcDomain] : [null, null];
            let retXDomain =
              xDomainCalcDomain != null ? [...xDomainCalcDomain] : [null, null];

            if (zoomedXAxisCalcDomain) {
              retXDomain = [
                lastDrawLocationCalcDomain.left,
                lastDrawLocationCalcDomain.right,
              ];
            }

            const filteredLinesCalcDomain = linesCalcDomain.filter(
              line => !!line
            );
            const filteredPoints = _.flatMap(filteredLinesCalcDomain, line => {
              let points = line.data;
              if (zoomedXAxisCalcDomain) {
                points = points.filter(
                  point =>
                    point.x >= lastDrawLocationCalcDomain.left &&
                    point.x <= lastDrawLocationCalcDomain.right
                );
              } else if (xDomain != null) {
                points = points.filter(
                  point =>
                    (xDomain[0] == null || point.x >= xDomain[0]) &&
                    (xDomain[1] == null || point.x <= xDomain[1])
                );
              }
              return points;
            });

            const [xMinRaw, xMaxRaw] = d3.extent(
              filteredPoints.map(point => point.x)
            );
            let xMin = xMinRaw || 0;
            let xMax = xMaxRaw || 0;

            let minYPoints = filteredPoints.map(point =>
              point.y0 != null ? Math.min(point.y0, point.y) : point.y
            );
            let maxYPoints = filteredPoints.map(point =>
              point.y0 != null ? Math.max(point.y0, point.y) : point.y
            );

            // TODO(aswanberg): Consider adding padding like tensorboard does.
            if (ignoreOutliersCalcDomain) {
              minYPoints = _.sortBy(minYPoints);
              maxYPoints = _.sortBy(maxYPoints);

              const lowerBound = d3.quantile(minYPoints, 0.05) || -Infinity;
              const upperBound = d3.quantile(maxYPoints, 0.95) || Infinity;

              minYPoints = minYPoints.filter(yVal => yVal >= lowerBound);
              maxYPoints = maxYPoints.filter(yVal => yVal <= upperBound);
            }

            let yMin = _.min(minYPoints) || 0;
            let yMax = _.max(maxYPoints) || 0;

            const xRange = xMax - xMin;
            const yRange = yMax - yMin;

            if (xRange === 0) {
              if (linesCalcDomain[0].type === 'heatmap') {
                // ridiculous hacky fix to single step heatmap
                xMin = -0.49998;
                xMax = 0.49999;
              } else {
                xMin -= Math.abs(xMin);
                xMax += xMax === 0 ? 1 : Math.abs(xMax);
              }
            }

            // arbitrary epsilon to account for artifacts when smoothing constant lines
            if (yRange < 1e-8) {
              yMin -= yMax === 0 ? 2 : Math.abs(yMin);
              yMax += yMax === 0 ? 2 : Math.abs(yMax);
            }

            if (yScale !== 'log') {
              // smart guess for when the x axis should be y=0
              const smartSnapFactor = zoomedXAxisCalcDomain ? 0.5 : 2;
              if (yMin > 0) {
                if (yMin < yRange * smartSnapFactor) {
                  yMin = 0;
                }
              }
              if (yMax < 0) {
                if (-yMax < yRange * smartSnapFactor) {
                  yMax = 0;
                }
              }
            }

            retXDomain[0] = retXDomain[0] == null ? xMin : retXDomain[0];
            retXDomain[1] = retXDomain[1] == null ? xMax : retXDomain[1];

            if (zoomedYAxisCalcDomain) {
              retYDomain = [
                lastDrawLocationCalcDomain.bottom,
                lastDrawLocationCalcDomain.top,
              ];
            } else {
              retYDomain[0] = retYDomain[0] == null ? yMin : retYDomain[0];
              retYDomain[1] = retYDomain[1] == null ? yMax : retYDomain[1];
            }

            return [retXDomain, retYDomain];
          }
        ),
      [yScale, xDomain]
    );

    const onWindowResize = useCallback(() => {
      setRecentResize(true);
    }, []);

    useEffect(() => {
      window.addEventListener('resize', onWindowResize);
      return () => {
        window.removeEventListener('resize', onWindowResize);
      };
    }, [onWindowResize]);

    let filteredLines: Line[] = lines.map(line => {
      return {
        ...line,
        data: line.data.filter(pt => _.isFinite(pt.x) && _.isFinite(pt.y)),
      };
    });

    if (xScale === 'log') {
      filteredLines = filteredLines.map(line => {
        return {
          ...line,
          data: line.data.filter(pt => pt.x > 0),
        };
      });
    }

    // NOTE: remove performs a mutation
    const hiddenLines = _.remove(filteredLines, line => line.data.length === 0);

    if (filteredLines.length === 0) {
      return <></>;
    }

    // Axis bounds specified by the user in config
    const userXDomain = xDomain;
    const userYDomain = yDomain;
    const [calcXDomain, calcYDomain] = calcDomain(
      filteredLines,
      userXDomain,
      userYDomain,
      lastDrawLocation,
      ignoreOutliers,
      zoomedXAxis,
      zoomedYAxis
    );

    // Make sure data is within the axis bounds set by the user
    if (
      (userXDomain != null &&
        ((userXDomain[1] != null &&
          // X bounds max is smaller than data min
          calcXDomain[0] > userXDomain[1]) ||
          (userXDomain[0] != null &&
            // X bounds min is bigger than data max
            calcXDomain[1] < userXDomain[0]))) ||
      (userYDomain != null &&
        ((userYDomain[1] != null &&
          // Y bounds max is smaller than data min
          calcYDomain[0] > userYDomain[1]) ||
          (userYDomain[0] != null &&
            // Y bounds min is bigger than data max
            calcYDomain[1] < userYDomain[0])))
    ) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            justifyContent: 'center',
          }}>
          Data out of bounds. Try adjusting the axis range.
        </div>
      );
    }

    // After calculating the domain, let's filter the lines a bit again. We want to avoid
    // a bug (https://github.com/wandb/core/issues/2323) where lines aren't drawn when
    // there are extreme outliers present. So let's clamp points to an offset of the domain.
    // We'll still use the unclamped points in the crosshair so as to not obviously present "wrong" data.
    const yDomainLowerBound = calcYDomain[0] - 100000;
    const yDomainUpperBound = calcYDomain[1] + 100000;
    const clampedFilteredLines = filteredLines.map(line => {
      return {
        ...line,
        data: line.data.map(pt => {
          let newY = Math.max(pt.y, yDomainLowerBound);
          newY = Math.min(pt.y, yDomainUpperBound);
          if (pt.y !== newY) {
            return {...pt, y: newY};
          }

          return pt;
        }),
      };
    });

    return (
      <div
        ref={domRef}
        className="line-plot"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        {showLegend && (
          <div
            className="line-plot-legend"
            style={{
              fontSize: 12,
              flexShrink: 0,
              lineHeight: '110%',
              fontFamily: 'Inconsolata, monospace',
              color: '#888 !important',
              display: 'flex',
              textAlign: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}>
            {legendPrefix}
            {lines
              .filter(line => !line.aux)
              .map((line, i) => {
                const hidden: boolean =
                  _.find(hiddenLines, l => l.uniqueId === line.uniqueId) !=
                  null;
                const color = !hidden ? line.color : '#aaa';

                return (
                  <span
                    className={classNames(hidden && 'line-plot-title-disabled')}
                    key={'line' + i}
                    style={{
                      marginRight: 8,
                      marginLeft: 8,
                      maxWidth: '100%',
                    }}>
                    <span
                      key={'line-plot-color' + i}
                      className="line-plot-color"
                      style={{display: 'inline', color}}>
                      {markToIcon(line.mark || 'solid')}
                    </span>
                    <span
                      className="line-plot-title"
                      style={{color: line.color}}
                      key={line.title}>
                      {line.fancyTitle &&
                        FancyLegend({
                          ...line.fancyTitle,
                          disableRunLink: disableRunLinks,
                        })}
                    </span>
                  </span>
                );
              })}
          </div>
        )}

        <DelayedRender>
          <div
            className={
              'plot-container line-plot-container' +
              (lastDrawLocation ? lastDrawLocation.classes : '')
            }
            style={{height: 0, flexGrow: 1}}>
            <LinePlotPlot
              xAxis={xAxis}
              yAxis={yAxis}
              xAxisTitle={xAxisTitle}
              yAxisTitle={yAxisTitle}
              xDomain={calcXDomain}
              yDomain={calcYDomain}
              yScale={yScale || 'linear'}
              xScale={xScale || 'linear'}
              lines={clampedFilteredLines}
              lastDrawLocation={lastDrawLocation}
              domRef={domRef}
              svg={svg}
            />
            <div
              style={{
                position: 'absolute',
                top: 0,
                width: '100%',
                height: '100%',
              }}>
              <LinePlotCrosshair
                xDomain={
                  filteredLines[0].type === 'heatmap'
                    ? [calcXDomain[0] - 0.5, calcXDomain[1] + 0.5]
                    : calcXDomain
                }
                yDomain={calcYDomain}
                yScale={yScale || 'linear'}
                xScale={xScale || 'linear'}
                xAxis={xAxis}
                yAxis={yAxis}
                lines={filteredLines}
                lastDrawLocation={lastDrawLocation}
                onMouseDown={(e: any) => {
                  setHideCrosshair(true);
                  setRecentResize(false);
                }}
                onMouseUp={() => setHideCrosshair(false)}
                hideCrosshair={hideCrosshair}
                onBrushEnd={(
                  area: any,
                  newZoomedXAxis: boolean,
                  newZoomedYAxis: boolean
                ) => {
                  setLastDrawLocation(area);
                  setZoomedXAxis(newZoomedXAxis);
                  setZoomedYAxis(newZoomedYAxis);
                  if (zoomCallback != null) {
                    let left = area != null ? area.left : null;
                    let right = area != null ? area.right : null;
                    if (timestep != null) {
                      if (left != null) {
                        left = convertTimestepToSeconds(left, timestep);
                      }
                      if (right != null) {
                        right = convertTimestepToSeconds(right, timestep);
                      }
                    }
                    zoomCallback(left, right);
                  }
                }}
              />
            </div>
          </div>
        </DelayedRender>
      </div>
    );
  },
  {
    id: 'LinePlot',
    memo: makePropsAreEqual({
      name: 'LinePlot',
      deep: ['yDomain', 'xDomain'],
      ignoreFunctions: true,
      debug: false,
      verbose: false,
    }),
  }
);

export default LinePlot;
